<script setup lang="ts">
    import KButton from "@/components/Button.vue";
    import { generateSchema } from "@/components/forms/Form/schemaHelpers";
    import KFilter from "@/components/KFilter/KFilter.vue";
    // For Pycharm users
    // noinspection ES6UnusedImports
    import moment from "moment";
    import { onMounted, ref } from "vue";

    const props = defineProps<{
        /**
         * Startvärde i formuläret
         */
        defaultValue?: {
            [key: string]: any;
        };
        /**
         * Ska filtrering emittas vid mount?
         */
        filterOnMount?: boolean;
        keepOnClear?: string[];
        tableStateName?: string;
    }>();

    const filterRef = ref();

    defineExpose({
        setFieldValue: (props: {
            name: string; // Namn på fältet
            value: unknown; // Nya värdet
        }) => filterRef?.value?.setFieldValue?.(props),
        manualSubmit: () => filterRef?.value?.manualSubmit?.(),
    });

    const emit = defineEmits<{
        (event: "submit", payload: { value: any }): void;
    }>();

    const schema = generateSchema({
        addToAll: {
            ignoreEmptyData: true,
        },
        schema: [
            {
                component: "KDateTimepicker",
                name: "bokad_operation_datum__gte",
                label: "Operationsdatum fr.o.m.",
            },
            {
                component: "KDateTimepicker",
                name: "bokad_operation_datum__lte",
                label: "Operationsdatum t.o.m.",
            },
            {
                component: "KText",
                name: "patient__personId",
                label: "PersonID",
            },
            {
                component: "KText",
                name: "patient__namn",
                label: "Patientnamn",
            },
            {
                component: "ProfileAutocomplete",
                name: "current_huvudoperator",
                label: "Operatör",
                allowSearch: true,
                urlParams: {
                    roll__code: "kirurg",
                },
                autoPosition: true,
                multiple: true,
            },
            {
                component: "HuvudoperationskodAutocomplete",
                name: "current_huvudoperationskod",
                label: "Huvudoperationskod",
                autoPosition: true,
                required: false,
                multiple: true,
            },
            {
                component: "TypoperationAutocomplete",
                name: "current_typoperation",
                label: "Typoperation",
                autoPosition: true,
                retrieveOptionsOnMount: true,
                multiple: true,
            },
            {
                component: "KAutocomplete",
                name: "current_sal",
                label: "Sal",
                url: "/operation/salar/",
                optionMapper: ({ id, namn }: { id: string; namn: string }) => ({ value: id, label: namn }),
                autoPosition: true,
                multiple: true,
            },
            {
                component: "KText",
                name: "opnr",
                label: "Operationsnummer",
            },
            {
                component: "OperationStatusAutocomplete",
                multiple: true,
                autoPosition: true,
                useFilter: false,
            },
            {
                component: "OrgunitAutocomplete",
                name: "operationsenhet",
                label: "Operationsenhet",
                autoPosition: true,
                urlParams: { vardenhetstyp: "operationsenhet" },
                multiple: true,
            },
            {
                component: "VardformAutocomplete",
                label: "Vårdform",
                autoPosition: true,
                multiple: true,
            },
        ],
    });

    const onFilter = ({ formData }: { formData: any }) => {
        emit("submit", { value: formData });
    };

    onMounted(async () => {
        if (props.filterOnMount) {
            emit("submit", { value: await filterRef?.value?.manualSubmit?.() });
        }
    });
</script>

<template>
    <KFilter
        ref="filterRef"
        name="operationsoversikt"
        searchPlaceholder="Sök operationen"
        :schema="schema"
        :defaultValue="defaultValue"
        :keepOnClear="keepOnClear"
        :tableStateName="tableStateName"
        @submit="onFilter"
    >
        <template #quick-actions="{ callback }">
            <KButton
                type="button"
                variant="selectable"
                size="adaptable"
                @click="
                    callback({
                        values: {
                            bokad_operation_datum__gte: moment().format('YYYY-MM-DD'),
                            bokad_operation_datum__lte: moment().endOf('isoWeek').format('YYYY-MM-DD'),
                        },
                        keep: ['operationsenhet'],
                    })
                "
            >
                Denna vecka
            </KButton>
            <KButton
                type="button"
                variant="selectable"
                size="adaptable"
                @click="
                    callback({
                        values: {
                            bokad_operation_datum__gte: moment().startOf('isoWeek').add(1, 'week').format('YYYY-MM-DD'),
                            bokad_operation_datum__lte: moment().endOf('isoWeek').add(1, 'week').format('YYYY-MM-DD'),
                        },
                        keep: ['operationsenhet'],
                    })
                "
            >
                Nästa vecka
            </KButton>
            <KButton
                type="button"
                variant="selectable"
                size="adaptable"
                @click="
                    callback({
                        values: {
                            bokad_operation_datum__gte: moment().format('YYYY-MM-DD'),
                            bokad_operation_datum__lte: moment().format('YYYY-MM-DD'),
                        },
                        keep: ['operationsenhet'],
                    })
                "
            >
                Idag
            </KButton>
            <KButton
                type="button"
                variant="selectable"
                size="adaptable"
                @click="
                    callback({
                        values: {
                            bokad_operation_datum__gte: moment().add(1, 'day').format('YYYY-MM-DD'),
                            bokad_operation_datum__lte: moment().add(1, 'day').format('YYYY-MM-DD'),
                        },
                        keep: ['operationsenhet'],
                    })
                "
            >
                Imorgon
            </KButton>
            <KButton
                type="button"
                variant="selectable"
                size="adaptable"
                @click="callback({ values: {}, keep: ['operationsenhet'] })"
            >
                Alla
            </KButton>
        </template>
    </KFilter>
</template>

<style scoped lang="scss">
    .operationsoversikt-filter {
    }
</style>
